// In roomGames.js
import axios from 'axios';

declare let booking_object: any;

export default class roomGames{

    /**
     * Get games data from the server.
     * This function now returns the Axios promise.
     * @returns {Promise<any>}
     */
    get(roomId: string, val_date: string, timeSlot: string | null, players: number | null): Promise<any> {

        // We convert players to string for the request.
        const playersStr = players !== null ? String(players) : '1';

        const post_data = {
            'room_id': roomId || '',
            'date': val_date,
            'start_time': timeSlot || '',
            'players': playersStr,
        };

        const queryString = new URLSearchParams(post_data).toString();

        // Return the entire axios promise.
        // The caller will handle .then() and .catch().
        return axios.post(booking_object.rest_urls.get_room_games, queryString, {
            validateStatus: (status) => status == 200
        });

    }

    /**
     * Shows a loading spinner inside the target element.
     * @param {string} targetSelector - The CSS selector of the element to inject the preloader into.
     */
    showPreloader(targetSelector: string) {
        
        const targetElement = document.querySelector(targetSelector);

        if(!targetElement) return;

        const preloaderHTML = `
            <div class="flex flex-col items-center justify-center min-h-[300px] text-white">
                <div class="w-12 h-12 border-4 border-main-rose-600 border-t-transparent rounded-full animate-spin" role="status"></div>
                <p class="mt-4 fluid-text-base font-semibold">Caricamento giochi...</p>
            </div>
        `;

        targetElement.innerHTML = preloaderHTML;

    }

    /**
     * Shows an error message inside the target element.
     * @param {string} targetSelector - The CSS selector of the element to inject the error message into.
     * @param {string} message - The error message to display.
     */
    showError(targetSelector: string, message: string = "Si è verificato un errore imprevisto."){

        const targetElement = document.querySelector(targetSelector);
        if (!targetElement) return;

        const errorHTML = `
            <div class="flex items-center justify-center min-h-[300px] bg-main-rose-100 border border-main-rose-200 rounded-lg p-4">
                <p class="text-main-rose-600 font-semibold">${message}</p>
            </div>
        `;

        targetElement.innerHTML = errorHTML;

    }

    /**
     * Renders all game categories into a target DOM element.
     * @param {object} data - The data object containing { available, not_available, ... }.
     * @param {string} targetSelector - The CSS selector of the element to inject the HTML into.
     */
    renderGames(data: any, targetSelector: string){

        const targetElement = document.querySelector(targetSelector);

        if(!targetElement){
            
            console.error(`Target element "${targetSelector}" not found for rendering games.`);
            return;

        }

        let finalHTML = '';

        // Create HTML for each category, but only if the array is not empty.
        finalHTML += this._createCategoryRowHTML(
            'Disponibili', 
            data.available, 
            { cardType: 'available', titleColor: 'text-semantic-green-300' }
        );

        finalHTML += this._createCategoryRowHTML(
            'Minimo giocatori non raggiunto', 
            data.players_below_minimum,
            { cardType: 'minimum_players', titleColor: 'text-semantic-yellow-300' }
        );
        
        finalHTML += this._createCategoryRowHTML(
            'Non Disponibili', 
            data.not_available,
            { cardType: 'unavailable', titleColor: 'text-semantic-red-300' }
        );

        targetElement.innerHTML = finalHTML;

    }
    
    /**
     * Creates the HTML for a full category row (title + grid of game cards).
     * Returns an empty string if the games array is empty.
     * @private
     */
    _createCategoryRowHTML(title: string, games: any[], options: any = {}): string {

        // If the games array is null or empty, return nothing.
        if (!games || games.length === 0) {
            return '';
        }

        // Map over each game to create its card HTML
        const cardsHTML = games.map(game => this._createGameCardHTML(game, options.cardType)).join('');

        // Return the full row HTML with title and grid container
        return `
            <div class="grid w-full mb-12">

                <strong class="fluid-text-lg font-bold mb-4 space-x-2"><i class="fa-solid fa-circle ${options.titleColor || 'text-gray-800'}"></i> <span class="text-neutral-50">${title}</span></strong>

                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    ${cardsHTML}
                </div>

            </div>
        `;

    }

    /**
     * Creates the HTML for a single game card.
     * @private
     */
    _createGameCardHTML(game: any, cardType: string = 'available'): string {

        const price = parseFloat(game.price).toFixed(0);
        
        let slotAvailable = '';
        let buttonHTML = '';
        let imageHTML = '';
        let minimum_players = '';
        let btn_info = '';

        if(game.featured_image && game.featured_image.src){
            
            imageHTML = `
                <img
                    width="${game.featured_image.width}"
                    height="${game.featured_image.height}"
                    src="${game.featured_image.src}" 
                    srcset="${game.featured_image.srcset || ''}" 
                    sizes="${game.featured_image.sizes || ''}"
                    alt="${game.featured_image.alt || game.game_name}"
                    class="w-full object-cover rounded-t-lg" 
                    loading="lazy"
                    decoding="auto"
                />`;
            
        }else{
            
            // Optional: Show a placeholder if no image is provided
            imageHTML = `<div class="w-full h-72 bg-neutral-700 rounded-t-lg mb-4 flex items-center justify-center text-white/50">No Image</div>`;

        }

        // Customize card based on its type
        switch(cardType){
            
            case 'available':

                slotAvailable = 'text-semantic-green-300';
                buttonHTML = `<li><button type="button" data-game="${game.game_id}" class="btn-room-booking block w-full py-2.5 px-4 rounded-lg appearance-none outline-none uppercase font-bold bg-main-rose-700 hover:bg-main-rose-600 border border-main-rose-800 transition-colors duration-500">Prenota</button></li>`;
                minimum_players = '';
                btn_info = `<button type="button" data-game="${game.game_id}" class="btn-room-booking-info block w-full py-2.5 px-4 rounded-lg appearance-none outline-none uppercase font-bold bg-neutral-700 hover:bg-neutral-600 border border-neutral-800 transition-colors duration-500"><i class="fa-solid fa-circle-info"></i></button>`;

            break;
            
            case 'minimum_players':

                slotAvailable = 'text-semantic-yellow-300';
                buttonHTML = '';
                minimum_players = (game.minimum_players > 1) ? `<p class="fluid-text-xs text-neutral-300">Minimo ${game.minimum_players} giocatori per giocare</p>` : '';
                btn_info = `<button type="button" data-game="${game.game_id}" class="btn-room-booking-info block w-full py-2.5 px-4 rounded-lg appearance-none outline-none uppercase font-bold bg-neutral-700 hover:bg-neutral-600 border border-neutral-800 transition-colors duration-500"><i class="fa-solid fa-circle-info"></i></button>`;

            break;
            
            case 'unavailable':

                slotAvailable = 'text-semantic-red-300';
                buttonHTML = '';
                minimum_players = (game.minimum_players > 1) ? `<p class="fluid-text-xs text-neutral-300">Minimo ${game.minimum_players} giocatori per giocare</p>` : '';
                btn_info = `<button type="button" data-game="${game.game_id}" class="btn-room-booking-info block w-full py-2.5 px-4 rounded-lg appearance-none outline-none uppercase font-bold bg-neutral-700 hover:bg-neutral-600 border border-neutral-800 transition-colors duration-500"><i class="fa-solid fa-circle-info"></i></button>`;

            break;
            
        }

        return `
            <div class="flex flex-col rounded-lg bg-main-rose-950/20 border border-neutral-800/70">

                ${imageHTML}

                <div class="flex-grow pt-4 px-4 pb-2">

                    <span class="block w-full space-x-1 mb-1 fluid-text-xs text-neutral-300"><i class="fa-solid fa-clock"></i> <span>${game.playtime} minuti</span></span>
                    <strong class="fluid-text-lg font-medium text-white">${game.game_name}</strong>

                    <div class="text-white space-y-1">
                        <p class="fluid-text-sm ${slotAvailable}"> ${game.slots_available} posti disponibili</p>
                        ${minimum_players}
                    </div>

                </div>

                <div class="flex flex-row justify-between items-center px-4 pb-4 space-x-2">
                    <div><span class="text-neutral-300"><span class="fluid-text-xl font-medium text-white">${price}€</span> a persona</span></div>
                    <div>
                        <ul class="flex flex-row space-x-2 items-center">
                            <li>${btn_info}</li>
                            ${buttonHTML}
                        </ul>
                    </div>
                </div>
                
            </div>
        `;

    }

}